import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@src/lib/utils/cn";
import { textV4Variants } from "@src/lib/ui/Text";

const TooltipProvider = ({
  delayDuration = 300,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
);

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * The bubble's surface and type, shared by TooltipContent, TooltipGroup and
 * the static specimens on /ui. `textV4Variants` brings `transition-all`;
 * tooltips animate with keyframes only, so it is switched off.
 */
const tooltipSurfaceClassName = cn(
  "rounded-md bg-popover px-2.5 py-1.5 text-popover-foreground shadow-large",
  textV4Variants({ variant: "helper-description" }),
  "transition-none",
);

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-[9999] max-w-64 overflow-hidden whitespace-normal break-words",
        tooltipSurfaceClassName,
        // Appear fades and scales up from --tt-scale over --tt-in-dur; leave is
        // the reverse over the shorter --tt-out-dur (tokens in global.css).
        "origin-[var(--radix-tooltip-content-transform-origin)] data-[state=delayed-open]:animate-tooltip-in data-[state=instant-open]:animate-tooltip-in data-[state=closed]:animate-tooltip-out motion-reduce:!animate-none",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** Keyboard hint after the label, e.g. `Dictation <TooltipShortcut>⌃D</TooltipShortcut>`. */
const TooltipShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn("ml-1 text-muted-foreground", className)} {...props} />
);
TooltipShortcut.displayName = "TooltipShortcut";

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipShortcut,
  tooltipSurfaceClassName,
};
